import React, { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'

export interface TableExportRegistration {
  id: string
  onExport: () => void | Promise<void>
  onExportAll?: () => void | Promise<void>
  onExportTable?: () => void | Promise<void>
  isExporting: boolean
  disabled: boolean
}

interface TableExportContextValue {
  registration: TableExportRegistration | null
  registerTableExport: (registration: TableExportRegistration) => void
  unregisterTableExport: (id: string) => void
  hasPageHeader: boolean
  registerPageHeader: () => void
  unregisterPageHeader: () => void
  hasTable: boolean
  registerTable: (id?: string) => void
  unregisterTable: (id?: string) => void
}

const TableExportContext = createContext<TableExportContextValue | null>(null)

export const TableExportProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [registration, setRegistration] = useState<TableExportRegistration | null>(null)
  const [headerCount, setHeaderCount] = useState(0)
  const [tableCount, setTableCount] = useState(0)

  const registerTableExport = useCallback((next: TableExportRegistration) => {
    setRegistration(next)
  }, [])

  const unregisterTableExport = useCallback((id: string) => {
    setRegistration((current) => (current?.id === id ? null : current))
  }, [])

  const registerPageHeader = useCallback(() => {
    setHeaderCount((count) => count + 1)
  }, [])

  const unregisterPageHeader = useCallback(() => {
    setHeaderCount((count) => Math.max(0, count - 1))
  }, [])

  const registerTable = useCallback((_id?: string) => {
    setTableCount((count) => count + 1)
  }, [])

  const unregisterTable = useCallback((_id?: string) => {
    setTableCount((count) => Math.max(0, count - 1))
  }, [])

  const value = useMemo(
    () => ({
      registration,
      registerTableExport,
      unregisterTableExport,
      hasPageHeader: headerCount > 0,
      registerPageHeader,
      unregisterPageHeader,
      hasTable: tableCount > 0,
      registerTable,
      unregisterTable,
    }),
    [
      registration,
      registerTableExport,
      unregisterTableExport,
      headerCount,
      registerPageHeader,
      unregisterPageHeader,
      tableCount,
      registerTable,
      unregisterTable,
    ],
  )

  return <TableExportContext.Provider value={value}>{children}</TableExportContext.Provider>
}

export const useTableExportContext = () => useContext(TableExportContext)
